"use client";

import clsx from "clsx";
import type { ReactNode } from "react";
import { useModals } from "./ModalProvider";

type Props = {
  /** The modal's name, as triggers open it (data-modal in the theme). */
  id: string;
  /**
   * The theme's modal--signin (which modal--forgot-password duplicated): the
   * box sits near the top of the overlay rather than in its middle, rounder
   * and narrower, with a quiet close button.
   */
  variant?: "default" | "signin";
  /** The theme's modal--dark: a dark overlay in place of the light one. */
  dark?: boolean;
  children: ReactNode;
};

/**
 * The modal chrome the theme's fragments/modals/*.php shared: an overlay that
 * is always in the page and shown with data-active, a content box, and a close
 * button.
 *
 * As in modal.js it closes from its close button, from a click on the overlay
 * itself (not on anything inside it), and on Escape, which the provider
 * handles.
 *
 * The theme's close button drew its cross with the Material Icons "close"
 * ligature, but that font is not loaded here, so it is the same glyph as an
 * inline SVG.
 */
export default function Modal({
  id,
  variant = "default",
  dark = false,
  children,
}: Props) {
  const { active, closeModal } = useModals();
  const isActive = active === id;
  const signin = variant === "signin";

  return (
    <div
      className={clsx(
        "fixed top-0 left-0 z-9999999 flex h-full w-full justify-center",
        "invisible pointer-events-none opacity-0 transition-[opacity,visibility] duration-400 ease-[ease]",
        "data-[active=true]:visible data-[active=true]:pointer-events-auto data-[active=true]:opacity-100",
        dark ? "bg-black/80" : "bg-white/80",
        signin
          ? "items-start px-0 py-25 mobile:py-12.5"
          : "items-center p-[15px] mobile:p-0",
        "js-modal",
      )}
      data-modal={id}
      data-active={isActive}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeModal();
        }
      }}
    >
      <div
        className={clsx(
          "relative max-h-screen w-full overflow-y-auto bg-white shadow-[0_0_20px_rgba(0,0,0,0.1)]",
          signin
            ? "max-w-[500px] rounded-[20px] px-7.5 py-12.5 mobile:px-[15px] mobile:py-5"
            : "max-w-[600px] rounded-[4px]",
        )}
      >
        <button
          type="button"
          className={clsx(
            "absolute top-2.5 right-2.5 size-10 cursor-pointer border-0 p-0 leading-none",
            "transition-[background,color] duration-400 ease-[ease]",
            signin
              ? "rounded-full bg-transparent text-[#ccc] hover:bg-[#eee]"
              : "rounded-[4px] bg-primary text-white hover:bg-[#303597]",
            "js-close-modal",
          )}
          aria-label="Close"
          onClick={closeModal}
        >
          <svg
            className="mx-auto block"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>

        {children}
      </div>
    </div>
  );
}
